<template>
  <ul id="flow-confirmation-list" v-if="dataItems">
    <li v-for="item in dataItems" v-if="item.value" :key="item.id">
      <div>
        <div class="label">{{ item.label }}</div>
        <div class="value">{{ item.value }}</div>
      </div>
      <a v-if="!dataChanged" :class="{ saving: fieldEdit.field === item.editId && isSaving }" @click="displayEditModal(item)">{{ i18n.t('base:edit') }}</a>
      <small v-if="item.hint">{{ item.hint }}</small>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
  @import '../assets/mixins';

  ul{
    list-style-type: none;
    margin: 20px -30px 25px;

    @include desktop-big {
      margin: 20px 0 25px;
    }

    li{
      border-top: 1px solid rgba(#343333, 0.1);
      padding: 12px 30px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;

      &:last-of-type {
        border-bottom: none;
      }

      &:first-of-type {
        border-top: none;
      }

      small {
        color: $buttonSecondaryColor;
        display: flex;
        font-size: 12px;
        letter-spacing: 0;
        line-height: 14px;
        display: flex;
        padding-left: 6px;
        border: solid 1px $buttonSecondaryColor;
        padding: 3px 17px 3px 10px;
        border-radius: 2px;
        align-items: center;
        margin-right: auto;
        align-self: flex-start;
        margin-top: 5px;
        &::before {
          content: "";
          height: 4px;
          width: 10px;
          border-top: 1.5px solid $buttonSecondaryColor;
          border-right: 1.5px solid $buttonSecondaryColor;
          transform: rotate(128deg);
          margin-right: 5px;
          margin-top: -3px;
        }
      }
    }

    /deep/ .label{
      color: $confirmItemLabelColor;
      font-weight: $confirmItemLabelWeight;
      font-size: $confirmItemLabelSize;
      line-height: 22px;
    }

    /deep/ .value {
      font-size: 16px;
      color: #161616;
      font-weight: bold;
      line-height: 1.5rem; // 24px
      padding-right: 25px;
      max-width: 200px;
      
      @include small-phone {
        max-width: 175px;
      }
    }

    a {
      color: $blackTextColor;
      font-weight: bold;
      background-color: #EFF2F4;
      border-radius: 14px;
      text-decoration: none;
      padding: 8px 15px 8px 25px;
      font-size: 11px;
      cursor: pointer;
      position: relative;
      min-width: 59px;
      height: 28px;
      box-sizing: border-box;
      display: flex;
      justify-content: center;
      align-items: center;
      max-width: 70px;

      &::before {
        content: '';
        background-image: url('~static/edit.svg');
        width: 9px;
        height: 9px;
        margin-right: 3px;
        position: absolute;
        left: 10px;
        top: 8px;
      }

      &.saving {
        color: transparent;
        background: $savingTextColor;
        
        &::before {
          display: none;
        }
        &::after {
          content: '';
          width: 12px;
          height: 6px;
          border: 2px solid white;
          box-sizing: border-box;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 4px;
          margin: auto;
          border-right: none;
          border-top: none;
          transform: rotate(-45deg);
          animation: arrow-check .4s cubic-bezier(0,-0.05,0,1.01) forwards;
        }
      }
    }
  }

  @keyframes arrow-check {
    from {
      transform: rotate(-95deg);
    }
    
    to {
      transform: rotate(-45deg);
    }
  }

</style>

<script>
  import Vue from 'vue'
  import { mapState, mapMutations, mapActions } from 'vuex'
  import { getNestedProperty } from '../utils/structure.js'
  import partnerConfig from '../partners/partner.module.config'
  import RegistrationBonusEntry from './FlowListBonusEntry'

  export default Vue.component('flow-confirmation-list', {
    components: {
      'bonus-entry': RegistrationBonusEntry
    },
    props: ['screenParams', 'onlyEntries'],
    data () {
      return {
        isSaving: false
      }
    },
    watch: {
      'fieldEdit.updated' (v) {
        if (!v) return
        this.isSaving = v
        setTimeout(() => {
          this.isSaving = false
        }, 1500)
      }
    },
    computed: {
      ...mapState(['app', 'user', 'company', 'route', 'fieldEdit']),
      dataChanged () {
        return this.app.sofortDataChanged
      },
      dataItems () {
        const pathInfo = partnerConfig.path(this.i18n)
        const idinFieldsToCheck = partnerConfig.settings.idinFieldsToCheck.map(field => {
          if (field === 'birthdate') return 'masked-birthdate'
        })

        const dataItemsProcessed = pathInfo.reduce((screens, path) => {
          const filteredPathScreens = path.screens.filter(screen => {
            return this.app.country.code === 'NL' ? screen.flowConfirmation && idinFieldsToCheck.includes(screen.type) : screen.flowConfirmation
          })
          const splittedPathScreens = filteredPathScreens.filter(screen => screen.flowConfirmation && screen.flowConfirmation.splittedParts)
          let splittedEntries = []

          for (let i = 0; i < splittedPathScreens.length; i++) {
            const parts = splittedPathScreens[i].flowConfirmation.splittedParts
            if (parts.length > 0) {
              for (let ii = 0; ii < parts.length; ii++) {
                const screenObj = Object.assign({}, splittedPathScreens[i])
                screenObj.flowConfirmation = Object.assign({}, screenObj.flowConfirmation)
                screenObj.flowConfirmation.propertyName = parts[ii].propertyName
                screenObj.flowConfirmation.orderNo += ii
                screenObj.flowConfirmation.labelLocale = () => this.i18n.t(`flowConfirmation:${parts[ii].propertyName}`)
                if (parts[ii].path) screenObj.type = parts[ii].path
                delete screenObj.flowConfirmation.splittedParts
                splittedEntries.push(screenObj)
              }
            }
          }
          const pathScreens = filteredPathScreens
            .concat(splittedEntries)
            .filter(screen => {
              let includeOnlySpecifiedFields = true
              if (this.onlyEntries && this.onlyEntries.length > 0) {
                includeOnlySpecifiedFields = this.onlyEntries.includes(screen.type.replace('.', '-'))
              }
              return !screen.flowConfirmation.splittedParts && includeOnlySpecifiedFields
            })
            .map(screen => this.processScreen(path, screen))
          return screens.concat(pathScreens)
        }, [])

        const dataItemsSorted = dataItemsProcessed.sort((itemA, itemB) => itemA.orderNo - itemB.orderNo)

        const dataItemsGrouped = dataItemsSorted.reduce((groupedItems, item) => {
          const groupPioneer = item.groupId && groupedItems.findIndex(groupItem => groupItem.groupId === item.groupId)

          if (!item.groupId || groupPioneer < 0) {
            if (item.groupFormat) item.value = item.groupFormat.replace(item.formatId, item.value)
            groupedItems.push(item)
            return groupedItems
          }

          if (item.value) {
            const groupFormat = groupPioneer > 0 && groupedItems[groupPioneer].groupFormat
            if (groupFormat) groupedItems[groupPioneer].value = groupedItems[groupPioneer].value.replace(item.formatId, item.value)
            else groupedItems[groupPioneer].value = `${groupedItems[groupPioneer].value} ${item.value}`
          }
          groupedItems[groupPioneer].editEndPath = item.path

          return groupedItems
        }, [])

        return dataItemsGrouped && dataItemsGrouped.length > 0 && dataItemsGrouped
      }
    },
    mounted () {
      this.$store.dispatch('updateApp', {summaryEditMode: true})
    },
    methods: {
      ...mapMutations(['updateRoutingInfo']),
      ...mapActions(['next']),
      updateEditRouting (editStartPath, editEndPath) {
        this.$store.dispatch('updateApp', {summaryEditMode: true})
        this.updateRoutingInfo({
          editEndPath,
          flowConfirmationScreenPath: this.route.fullPath
        })
        this.next(editStartPath)
      },
      displayEditModal (item) {
        this.$store.commit('updateFieldEdit', {
          active: true,
          field: item.editId
        })
      },
      processScreen (path, screen) {
        const screenFormatted = {}
        const screenPath = screen.type.replace('.', '-')
        const screenPropFromType = screen.type.indexOf('-') !== -1 ? screen.type.replace(/(-\w)/, match => match.replace(/-/, '').toUpperCase()) : screen.type
        const storeKeyFromDispatchKey = screen.params && screen.params.dispatchKey && screen.params.dispatchKey.replace('update', '').toLowerCase()
        const titleFromParams = screen.params && screen.params.title && screen.params.title()
        const storeKey = screen.flowConfirmation.rootEntity || storeKeyFromDispatchKey || 'user'
        const dataProp = screen.flowConfirmation.propertyName || (screen.params && screen.params.propertyName) || screenPropFromType
        const value = getNestedProperty(this.$store.state[storeKey], dataProp)
        const groupFormat = screen.flowConfirmation.groupFormat
        const hint = screen.flowConfirmation.hint && screen.flowConfirmation.hint(this.$store)

        screenFormatted['path'] = `${path.path}/${screenPath}`
        screenFormatted['value'] = this.formatValue(value, dataProp)
        screenFormatted['editId'] = screen.flowConfirmation.editId || screen.type.split('.')[0]
        screenFormatted['formatId'] = screen.flowConfirmation.formatId || screen.type.split('.')[0]
        screenFormatted['editStartPath'] = screen.flowConfirmation.editStartPath || screenFormatted.path
        screenFormatted['editEndPath'] = screen.flowConfirmation.editEndPath || screenFormatted.path
        screenFormatted['groupId'] = screen.flowConfirmation.groupId || (typeof screen.flowConfirmation === 'string' && screen.flowConfirmation) || null
        screenFormatted['orderNo'] = screen.flowConfirmation.orderNo || 9999999999
        screenFormatted['label'] = (screen.flowConfirmation.labelLocale && screen.flowConfirmation.labelLocale(this.$store)) || this.getDefaultLabel(dataProp) || titleFromParams
        screenFormatted['id'] = `${screenFormatted['path']}-${dataProp}`
        screenFormatted['groupFormat'] = groupFormat || null
        screenFormatted['hint'] = hint

        return screenFormatted
      },
      formatValue (value, prop) {
        if (this.isDate(value)) {
          return this.formatBirthdate(value)
        } else if (prop === 'nationality') {
          return this.getNation(value)
        } else if (prop === 'gender') {
          return this.i18n.t(`gender:${value}`) || value
        }
        return value
      },
      isDate (date) {
        return date instanceof Date
      },
      formatBirthdate (date) {
        let birthdate = new Date(date)
        let birthday = birthdate.getUTCDate()
        let birthmonth = birthdate.getUTCMonth() + 1

        this.birthDay = birthday < 9 ? '0' + birthday : birthday
        this.birthMonth = birthmonth < 9 ? '0' + birthmonth : birthmonth
        this.birthYear = birthdate.getUTCFullYear().toString().slice(2, 4)

        return this.app.country.datePattern ? this.app.country.datePattern
          .replace('DD', this.birthDay)
          .replace('MM', this.birthMonth)
          .replace('YY', this.birthYear) : [this.birthDay, this.birthMonth, this.birthYear].join('.')
      },
      getNation (value) {
        return this.i18n.t(`country:${value}`) || value
      },
      getDefaultLabel (propName) {
        const label = {
          firstName: 'flowConfirmation:name',
          postalCode: 'flowConfirmation:address',
          phone: 'flowConfirmation:mobile_number',
          birthdate: 'flowConfirmation:date_of_birth'
        }

        const localeProp = label[propName]
        const localePresent = localeProp && this.i18n.exists(localeProp)
        const locale = localePresent && this.i18n.t(localeProp)

        return locale
      }
    }
  })
</script>


// WEBPACK FOOTER //
// src/components/FlowConfirmationList.vue